import { Aide } from '@/components/ui/aide';
import { useSyncOuEnLigne } from '../context/SyncContext';
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatMontant } from '../lib/formats';
import { libelle, optionsLibelles, referenceLisible, texteSur } from '../lib/libelles';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { SearchableSelect } from './ui/searchable-select';
import { financialKey } from './FinancialStatement';

const REPLI_ERREUR = 'Action impossible pour le moment. Réessayez.';
// Montant lisible en FCFA; « À rapprocher » quand le serveur n’a pas de montant.
const amountLabel = (amount, currency = 'XOF') => amount == null ? 'À rapprocher' : formatMontant(amount, currency);
export default function ShortageFinancialActions({
  detail,
  onChanged
}) {
  const [parties, setParties] = useState({
      COMPANY: []
    }),
    [accounts, setAccounts] = useState([]),
    [shares, setShares] = useState([]),
    [comment, setComment] = useState(''),
    [currency, setCurrency] = useState('XOF'),
    [status, setStatus] = useState('ASSIGNED');
  const enLigne = useSyncOuEnLigne().isOnline !== false;
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [key, setKey] = useState(financialKey),
    [selected, setSelected] = useState(0),
    [kind, setKind] = useState('CLAIM'),
    [amount, setAmount] = useState(''),
    [account, setAccount] = useState('');
  const [original,setOriginal] = useState(''), [documentId,setDocumentId] = useState(''), [sourceOperations,setSourceOperations] = useState([]), [invoices,setInvoices] = useState([]);
  useEffect(() => {
    let active = true;
    const rows = response => response.data.items || response.data.data?.items || response.data.data || [];
    // `/drivers` refuse page_size > 500 : toutes les pages plutôt qu'une seule trop grande.
    const everyPage = path => fetchAllPages(async params => rows(await api.get(path, {
      params
    })));
    Promise.all([everyPage('/drivers'), everyPage('/clients'), api.get('/finance/suppliers'), api.get('/finance/accounts')]).then(responses => {
      if (!active) return;
      setParties({
        DRIVER: responses[0].items,
        CUSTOMER: responses[1].items,
        SUPPLIER: rows(responses[2]),
        COMPANY: []
      });
      setAccounts(rows(responses[3]));
    }).catch(e => active && setError(messageErreur(e, REPLI_ERREUR)));
    return () => {
      active = false;
    };
  }, []);
  const partyOptions = useMemo(() => Object.fromEntries(Object.entries(parties).map(([type, rows]) => [type, rows.map(party => ({ value: party.id, label: party.name || party.full_name || 'Sans nom', description: [party.city, party.phone].filter(Boolean).join(' · ') || undefined, keywords: [party.contact_name, party.tax_id, party.license_number] }))])), [parties]);
  const originalOptions = useMemo(() => sourceOperations.filter(row => row.kind === 'SUPPLIER_PAYMENT').map(row => ({ value: row.id, label: `${amountLabel(row.amount, row.currency)} · ${texteSur(row.details?.notes, 'Règlement fournisseur')}${row.occurred_at ? ` · ${formatDate(row.occurred_at)}` : ''}`, keywords: [row.occurred_at] })), [sourceOperations]);
  const invoiceOptions = useMemo(() => [{ value: '', label: 'Crédit disponible au compte client' }, ...invoices.map(row => ({ value: row.id, label: referenceLisible(row.invoice_number, 'Facture sans numéro'), description: row.balance_due != null ? `Reste dû ${amountLabel(row.balance_due, row.currency || undefined)}` : undefined }))], [invoices]);
  const edit = fn => {
    fn();
    setKey(financialKey());
  };
  const run = async fn => {
    setBusy(true);
    setError('');
    try {
      await fn();
      setKey(financialKey());
      await onChanged();
    } catch (e) {
      setError(messageErreur(e, REPLI_ERREUR));
    } finally {
      setBusy(false);
    }
  };
  const decision = detail.case?.financial_decision;
  const share = decision?.responsibilities?.[selected];
  useEffect(()=>{
    let active=true;
    setOriginal('');setDocumentId('');setSourceOperations([]);setInvoices([]);
    if(!share?.party_id)return;
    api.get('/finance/statement',{params:{party_type:share.party_type,party_id:share.party_id}}).then(response=>active && setSourceOperations(response.data.data.operations)).catch(error=>active && setError(messageErreur(error, REPLI_ERREUR)));
    if(share.party_type==='CUSTOMER')api.get('/invoices',{params:{client_id:share.party_id,page_size:1000}}).then(response=>{if(active)setInvoices((response.data.data || response.data.items || []).filter(row=>row.client_id===share.party_id && row.status!=='CANCELLED'));}).catch(error=>active && setError(messageErreur(error, REPLI_ERREUR)));
    return()=>{active=false;};
  },[share?.party_type,share?.party_id]);
  const methods = share?.party_type === 'DRIVER' ? {
    CLAIM: 'Créer la créance décidée',
    DRIVER_RETURN: 'Enregistrer une récupération reçue'
  } : share?.party_type === 'CUSTOMER' ? {
    CLAIM: 'Créer la créance décidée',
    CUSTOMER_RECEIPT: 'Enregistrer une récupération reçue',
    CUSTOMER_CREDIT: 'Émettre un avoir selon la décision'
  } : share?.party_type === 'SUPPLIER' ? {
    SUPPLIER_CREDIT: 'Enregistrer l’avoir fournisseur',
    SUPPLIER_REFUND: 'Enregistrer le remboursement reçu'
  } : {
    COMPANY_LOSS: 'Prise en charge par l’entreprise'
  };
  return <details className="rounded-lg border p-3"><summary className="cursor-pointer font-medium">Décision financière et récupération explicites</summary><div className="pt-3 space-y-3">
    <div><Aide titre="Décision financière" libelle="Comment ça marche">L’estimation ne crée aucun montant dû. Désignez les parties, puis confirmez séparément chaque opération. Les mouvements de stock restent dans le dossier physique.</Aide></div>
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {shares.map((row, index) => <div className="grid sm:grid-cols-3 gap-2" key={index}><select aria-label={`Partie ${index + 1}`} className="border rounded-md bg-background p-2" value={row.party_type} onChange={event => edit(() => setShares(old => old.map((item, i) => i === index ? {
          ...item,
          party_type: event.target.value,
          party_id: undefined
        } : item)))}>{optionsLibelles('shortageParty').map(({ value, label }) => <option key={value} value={value}>{label}</option>)}</select>{row.party_type !== 'COMPANY' && <SearchableSelect aria-label={`Tiers ${index + 1}`} className="border rounded-md bg-background p-2" value={row.party_id || ''} onValueChange={value => edit(() => setShares(old => old.map((item, i) => i === index ? {
          ...item,
          party_id: value
        } : item)))} options={partyOptions[row.party_type] || []} placeholder="Choisir la partie concernée" searchPlaceholder="Rechercher un tiers…" emptyMessage="Aucun tiers trouvé" />}<Input aria-label={`Montant décidé ${index + 1}`} type="number" min="0" placeholder="Montant décidé" value={row.amount} onChange={event => edit(() => setShares(old => old.map((item, i) => i === index ? {
          ...item,
          amount: event.target.value
        } : item)))} /><Button variant="outline" onClick={() => edit(() => setShares(old => old.filter((_, i) => i !== index)))}>Retirer la partie</Button></div>)}
    <Button variant="outline" size="sm" onClick={() => edit(() => setShares(old => [...old, {
        party_type: 'COMPANY',
        amount: ''
      }]))}>Ajouter une partie</Button>
    <div className="grid sm:grid-cols-3 gap-3"><label className="text-sm">Traitement<select className="block w-full border rounded-md bg-background p-2" value={status} onChange={event => edit(() => setStatus(event.target.value))}>{['ASSIGNED', 'SHARED', 'COMPANY_ACCEPTED', 'RECOVERY_IN_PROGRESS'].map(code => <option key={code} value={code}>{libelle('shortageCase', code)}</option>)}</select></label><label className="text-sm">Monnaie<select className="block w-full border rounded-md bg-background p-2" value={currency} onChange={event => edit(() => setCurrency(event.target.value))}>{[...new Set(['XOF', 'EUR', 'USD', currency])].map(code => <option key={code} value={code}>{{ XOF: 'FCFA', EUR: 'Euro', USD: 'Dollar' }[code] || code}</option>)}</select></label><label className="text-sm">Motif<Input value={comment} onChange={event => edit(() => setComment(event.target.value))} /></label></div>
    <Button disabled={!enLigne || busy || !comment.trim() || !shares.length} onClick={() => run(() => api.post(`/shortages/trips/${detail.trip.id}/events`, {
        idempotency_key: key,
        expected_revision: detail.case.measurement_revision,
        event_type: 'DECISION',
        case_status: status,
        comment,
        currency,
        responsibilities: shares.map(row => ({
          ...row,
          amount: Number(row.amount)
        }))
      }))}>Enregistrer la décision sans effet financier</Button>
    {decision?.responsibilities?.length > 0 && <div className="border-t pt-3 space-y-3"><p className="font-medium">Exécuter une conséquence financière de la décision courante</p><select aria-label="Partie de la décision" className="border rounded-md bg-background p-2 w-full" value={selected} onChange={event => edit(() => {
          setSelected(Number(event.target.value));
          setKind('');
        })}>{decision.responsibilities.map((row, index) => <option key={index} value={index}>{row.party_type === 'COMPANY' ? 'Entreprise' : (parties[row.party_type] || []).find(party => party.id === row.party_id)?.name || (parties[row.party_type] || []).find(party => party.id === row.party_id)?.full_name || libelle('shortageParty', row.party_type)} · {amountLabel(row.amount, decision.currency)}</option>)}</select><select aria-label="Conséquence financière" className="border rounded-md bg-background p-2 w-full" value={kind} onChange={event => edit(() => setKind(event.target.value))}><option value="">Choisir l’opération</option>{Object.entries(methods).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><label className="text-sm block">Montant de cette opération<Input type="number" min="1" value={amount} onChange={event => edit(() => setAmount(event.target.value))} /></label><select aria-label="Compte pour récupération" className="border rounded-md bg-background p-2 w-full" value={account} onChange={event => edit(() => setAccount(event.target.value))}><option value="">Sans mouvement de trésorerie</option>{accounts.map(row => <option value={row.id} key={row.id}>{row.name}</option>)}</select>{kind==='SUPPLIER_REFUND' && <SearchableSelect aria-label="Règlement fournisseur d’origine" className="border rounded-md bg-background p-2 w-full" value={original || ''} onValueChange={value=>edit(()=>setOriginal(value))} options={originalOptions} placeholder="Choisir le règlement d’origine" searchPlaceholder="Rechercher un règlement…" emptyMessage="Aucun règlement trouvé" />}{kind==='CUSTOMER_CREDIT' && <SearchableSelect aria-label="Facture concernée par l’avoir" className="border rounded-md bg-background p-2 w-full" value={documentId || ''} onValueChange={value=>edit(()=>setDocumentId(value))} options={invoiceOptions} searchPlaceholder="Rechercher une facture…" emptyMessage="Aucune facture trouvée" />}<Button disabled={busy || !methods[kind] || !(Number(amount) > 0)} onClick={() => run(() => api.post(`/shortages/trips/${detail.trip.id}/financial-operations`, {
          expected_revision: detail.case.measurement_revision,
          operation: {
            kind,
            amount: Number(amount),
            currency: decision.currency,
            idempotency_key: key,
            ...(share.party_type === 'COMPANY' ? {} : {
              party_type: share.party_type,
              party_id: share.party_id
            }),
            ...(account ? {
              account_id: account
            } : {}),
            ...(original?{original_id:original}:{}),
            ...(documentId?{document_id:documentId}:{}),
            details: {
              reason: decision.comment
            }
          }
        }))}>Confirmer cette opération financière</Button></div>}
  </div></details>;
}
